<template>
  <Modal
    :show="visible"
    action-text="Apply"
    cancel-text="Cancel"
    :disabled="listLoading || !selectedTemplateUlid"
    width="60%"
    height="80%"
    content-class="apply-jp-template-content"
    @action="handleApplyTemplate"
    @close="close"
  >
    <template #title>
      Apply Template
    </template>
    <template #content>
      <div class="d-flex ga-6 h-100 pb-4">
        <DpplHeaderFooterContent class="w-50">
          <template #header>
            <div class="mb-4">
              Applying a template will replace the current content of this job profile
              with the selected template's content.
            </div>
          </template>

          <div class="d-flex flex-column ga-2 overflow-y-auto w-100">
            <v-sheet
              v-for="item in templatesWithDefaultLabeled"
              :key="item.ulid"
              class="template-list-item pa-3 rounded-lg border-sm cursor-pointer"
              :class="{ selected: selectedTemplateUlid === item.ulid }"
              @click="selectTemplate(item)"
            >
              <div class="text-body-2 font-weight-medium">
                {{ item.title }}
              </div>
              <div
                v-if="item.memo"
                class="text-body-3 text-textLight mt-1"
              >
                {{ item.memo }}
              </div>
            </v-sheet>
          </div>
        </DpplHeaderFooterContent>

        <v-divider vertical />

        <div class="w-50 h-100 overflow-y-auto pr-1">
          <div
            v-if="!selectedTemplate"
            class="d-flex align-center justify-center h-100"
          >
            <div class="text-body-2 text-textLight text-center">
              Select a template to see a preview
            </div>
          </div>

          <div
            v-else
            class="d-flex flex-column ga-3"
          >
            <div
              v-if="selectedTemplate.title"
              class="text-h5 font-weight-medium"
            >
              {{ selectedTemplate.title }}
            </div>

            <div
              v-for="field in previewFields"
              :key="field.type"
              class="py-2"
            >
              <div class="d-flex justify-end mt-2 mb-1">
                <FieldLabel>{{ field.label }}</FieldLabel>
              </div>
              <DocumentPreviewFieldHtml>
                <div
                  v-if="field.headerHtml && !isEmptyHtml(field.headerHtml)"
                  class="locked-field"
                >
                  <DpplSafeHtml :html="field.headerHtml" />
                </div>
                <DpplSafeHtml
                  v-if="field.html"
                  :html="field.html"
                />
                <div
                  v-if="field.footerHtml && !isEmptyHtml(field.footerHtml)"
                  class="locked-field"
                >
                  <DpplSafeHtml :html="field.footerHtml" />
                </div>
              </DocumentPreviewFieldHtml>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useJobProfiles } from '@/stores/jobProfiles/jobProfiles';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useModals } from '@/stores/modals/modals';
import Modal from '@/components/shared/Modal.vue';
import FieldLabel from '@/components/editor/field/FieldLabel.vue';
import DpplSafeHtml from '@ui/components/general/DpplSafeHtml/DpplSafeHtml.vue';
import DocumentPreviewFieldHtml from '@ui/components/general/DocumentPreviewFieldHtml/DocumentPreviewFieldHtml.vue';
import DpplHeaderFooterContent from '@ui/components/general/DpplHeaderFooterContent/DpplHeaderFooterContent.vue';
import { isEmptyHtml } from '@ui/helpers/htmlToText';
import { useTracking } from '@/stores/tracking/tracking';

const emit = defineEmits(['applied']);

const { show } = storeToRefs(useModals());
const { setModal } = useModals();
const { finalizedTemplates } = storeToRefs(useJobProfiles());
const { jobProfile } = storeToRefs(useCurrentJobProfile());
const { listLoading } = storeToRefs(useAppLoading());

const selectedTemplateUlid = ref(null);

const visible = computed(() => !!show.value.applyJobProfileTemplate);

const templatesWithDefaultLabeled = computed(() => (
  (finalizedTemplates.value || []).map((t) => ({
    ...t,
    title: t.is_default ? `${t.title} (Default)` : t.title,
  }))
));

const selectedTemplate = computed(() => (
  (finalizedTemplates.value || []).find((t) => t.ulid === selectedTemplateUlid.value) || null
));

const joinBlocksHtml = (blocks) => (blocks || []).map((block) => block.html).join('');

const previewFields = computed(() => {
  const fields = selectedTemplate.value?.fields || [];

  return fields.map((field) => ({
    type: field.type,
    label: field.label,
    html: joinBlocksHtml(field.blocks),
    headerHtml: joinBlocksHtml(field.header),
    footerHtml: joinBlocksHtml(field.footer),
  }));
});

const selectTemplate = (item) => {
  selectedTemplateUlid.value = item.ulid;
};

const close = () => {
  setModal({ field: 'applyJobProfileTemplate', value: false });
};

watch(visible, async (val) => {
  if (val) {
    selectedTemplateUlid.value = null;
    await useJobProfiles().loadFinalizedProfileTemplates();
    const first = (finalizedTemplates.value || [])[0];
    if (first && !selectedTemplateUlid.value) {
      selectedTemplateUlid.value = first.ulid;
    }
  } else {
    selectedTemplateUlid.value = null;
  }
});

const handleApplyTemplate = async () => {
  if (!selectedTemplateUlid.value) return;

  const profileUlid = jobProfile.value?.ulid;

  useTracking().jpEditorApplyTemplateClick({
    ulid: profileUlid,
    templateUlid: selectedTemplateUlid.value,
  });

  const result = await useCurrentJobProfile().applyTemplate({
    templateUlid: selectedTemplateUlid.value,
  });

  useTracking().jpEditorTemplateApplied({
    ulid: result.documentUlid,
    templateUlid: selectedTemplateUlid.value,
    newRevisionUlid: result.revisionUlid,
  });

  useAppSnackbar().addSuccessSnackbar({ text: 'Template applied successfully' });

  emit('applied', result);
  close();
};
</script>

<style scoped lang="less">
.template-list-item {
  user-select: none;

  &:hover {
    border-color: @brand-primary;
    background-color: fade(@brand-primary, 5%);
  }

  &.selected {
    border-color: @brand-primary;
    background-color: fade(@brand-primary, 10%);
    color: @brand-primary;
  }
}
</style>

<style lang="less">
.apply-jp-template-content {
  & > .v-sheet {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: 80vh;
  }
  .modal-content-wrapper {
    flex-grow: 1;
    min-height: 0;
    overflow: hidden;
    .content {
      overflow: hidden;
      height: 100%;
    }
  }
  .actions {
    width: 50%;
  }
}
</style>
